import React, { useCallback, useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import {
  ISelectedProtocol,
  IAppointmentsProgram,
} from "../../../../services/types/utils.types";
import {
  PROTOCOL_FREQUENCY_AFTER_EACH,
  TProtocolFrequencyType,
} from "../../../../services/types/treatmentProtocol.types";
import CompanyProgramsPopup from "../../../../components/modals/companyPrograms/companyProgramsPopup";
import ProtocolsManager from "../CreateAppointmentModal/ProtocolsManager";
import seriesStyles from "../CreateAppointmentModal/CreateAppointmentModal.module.css";
import styles from "./TreatmentPlanModal.module.css";

interface ISelectOption {
  value: number;
  label: string | number;
}

interface IProtocolOption {
  id: number;
  desc: string;
}

export interface ITreatmentPlanValues {
  appointmentsDurationsId: number;
  appointmentsSequenceId: number;
  protocols: ISelectedProtocol[];
  treatmentPlanName?: string; // Custom name; if empty, backend uses default
  roleId?: number;
  genderId?: number;
  migzarId?: number;
  therapistsSpecializationsIds?: number[];
  treatmentRoomId?: number;
  fundingSourcesId?: number;
  treatmentTypesId?: number;
  countOfAppointments?: number; // convert mode only — total sessions desired
}

interface ITreatmentPlanModalProps {
  companyId: number;
  repeatCount: number;
  appointmentsDurationsId: number;
  appointmentsSequenceId: number;
  calculatedDurationMinutes: number; // Computed from EndTime - StartTime
  selectedProtocols: ISelectedProtocol[];
  durationOptions: ISelectOption[];
  sequenceOptions: ISelectOption[];
  /** The frequency comes from the patient's device protocol and cannot be chosen here. */
  frequencyLocked?: boolean;
  protocols: IProtocolOption[];
  // Section 2 (plan details) options
  roleOptions?: ISelectOption[];
  genderOptions?: ISelectOption[];
  migzarOptions?: ISelectOption[];
  treatmentRoomOptions?: ISelectOption[];
  fundingSourcesOptions?: ISelectOption[];
  treatmentTypesOptions?: ISelectOption[];
  // Meeting mode is taken from the appointment's media (read-only display)
  mediaLabel?: string;
  // Previously-saved section-2 values, so reopening the modal keeps them
  initialDetails?: {
    treatmentPlanName?: string;
    roleId?: number;
    genderId?: number;
    migzarId?: number;
    treatmentRoomId?: number;
    fundingSourcesId?: number;
    treatmentTypesId?: number;
  };
  // "convert" turns an existing single appointment into a series — full config like a
  // series plan, plus a total-sessions selector. "create" is the default (from the
  // create-appointment flow, where the count is chosen outside this modal).
  mode?: "create" | "convert";
  initialProtocols?: ISelectedProtocol[];
  onClose: () => void;
  onSave: (values: ITreatmentPlanValues) => void;
}

const TreatmentPlanModal: React.FC<ITreatmentPlanModalProps> = ({
  companyId,
  repeatCount,
  appointmentsDurationsId,
  appointmentsSequenceId,
  calculatedDurationMinutes,
  selectedProtocols,
  durationOptions,
  sequenceOptions,
  frequencyLocked = false,
  protocols,
  roleOptions = [],
  genderOptions = [],
  migzarOptions = [],
  treatmentRoomOptions = [],
  fundingSourcesOptions = [],
  treatmentTypesOptions = [],
  mediaLabel = "",
  initialDetails,
  mode = "create",
  initialProtocols,
  onClose,
  onSave,
}) => {
  const isRtl = i18n.language === "he";
  const isConvert = mode === "convert";

  // Draft state — collected here, committed to the parent only on save.
  // Duration is NOT in draft state; it's computed from appointment start/end times.
  const [sequenceId, setSequenceId] = useState(appointmentsSequenceId);
  const [draftProtocols, setDraftProtocols] =
    useState<ISelectedProtocol[]>(initialProtocols ?? selectedProtocols);
  const [protocolsValid, setProtocolsValid] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  // Convert mode: total number of sessions the series should have.
  const [convertCount, setConvertCount] = useState(2);
  // Effective session count used for protocol cadence bounds and the payload.
  const effectiveCount = isConvert ? convertCount : repeatCount;

  // Optional sections start collapsed so the required fields (section 1) stand out.
  const [protocolsOpen, setProtocolsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Section 2 plan details (optional) — seeded from previously-saved values
  const [treatmentPlanName, setTreatmentPlanName] = useState<string>(initialDetails?.treatmentPlanName ?? "");
  const [roleId, setRoleId] = useState<number>(initialDetails?.roleId ?? 0);
  const [genderId, setGenderId] = useState<number>(initialDetails?.genderId ?? 0);
  const [migzarId, setMigzarId] = useState<number>(initialDetails?.migzarId ?? 0);
  const [treatmentRoomId, setTreatmentRoomId] = useState<number | null>(initialDetails?.treatmentRoomId ?? null);
  const [fundingSourcesId, setFundingSourcesId] = useState<number>(initialDetails?.fundingSourcesId ?? 0);
  const [treatmentTypesId, setTreatmentTypesId] = useState<number>(initialDetails?.treatmentTypesId ?? 0);

  // Convert is always series-like (full config). For a plain single, everything is
  // optional; frequency is required only for a series (or a convert).
  const isSeries = isConvert || repeatCount > 1;
  const canSave =
    calculatedDurationMinutes > 0 && (!isSeries || sequenceId > 0) && protocolsValid;

  const handleSave = useCallback(() => {
    if (!canSave) return;
    onSave({
      appointmentsDurationsId: 0, // Not used; duration is computed from appointment times
      appointmentsSequenceId: sequenceId,
      protocols: draftProtocols,
      treatmentPlanName: treatmentPlanName.trim() || undefined,
      roleId: roleId || undefined,
      genderId: genderId || undefined,
      migzarId: migzarId || undefined,
      treatmentRoomId: treatmentRoomId || undefined,
      fundingSourcesId: fundingSourcesId || undefined,
      treatmentTypesId: treatmentTypesId || undefined,
      countOfAppointments: isConvert ? convertCount : undefined,
    });
    onClose();
  }, [
    canSave,
    sequenceId,
    draftProtocols,
    treatmentPlanName,
    roleId,
    genderId,
    migzarId,
    treatmentRoomId,
    fundingSourcesId,
    treatmentTypesId,
    isConvert,
    convertCount,
    onSave,
    onClose,
  ]);

  // Prefill from an existing plan template. Frequency/protocol/details only —
  // duration is always derived from the appointment's start/end times, so the
  // template's own duration is intentionally ignored here.
  const handleTemplateSelect = useCallback(
    (program: Partial<IAppointmentsProgram>) => {
      if (program.appointmentsSequenceId) {
        setSequenceId(program.appointmentsSequenceId);
      }
      if (program.roleId) setRoleId(program.roleId);
      if (program.genderId) setGenderId(program.genderId);
      if (program.migzarId) setMigzarId(program.migzarId);
      if (program.treatmentRoomId) setTreatmentRoomId(program.treatmentRoomId);
      if (program.fundingSourcesId) setFundingSourcesId(program.fundingSourcesId);
      if (program.treatmentTypesId) setTreatmentTypesId(program.treatmentTypesId);

      const named = (protocolId: number) =>
        protocols.find((p) => p.id === protocolId)?.desc ?? "";

      if (program.protocols && program.protocols.length > 0) {
        // Multi-protocol model (AppointmentsProgramsProtocols) — pull all with cadence.
        setDraftProtocols(
          program.protocols.map((p) => {
            const n = p.everyNSessions ?? Math.min(3, effectiveCount);
            return {
              protocolId: p.protocolId,
              name: named(p.protocolId),
              frequencyType:
                (p.frequencyType as TProtocolFrequencyType) ?? PROTOCOL_FREQUENCY_AFTER_EACH,
              everyNSessions: Math.min(Math.max(1, n), effectiveCount),
            };
          }),
        );
      } else if (program.protocolId) {
        // Legacy single-protocol column fallback.
        setDraftProtocols([
          {
            protocolId: program.protocolId,
            name: named(program.protocolId),
            frequencyType: PROTOCOL_FREQUENCY_AFTER_EACH,
            everyNSessions: Math.min(3, effectiveCount),
          },
        ]);
      }
      setShowTemplates(false);
    },
    [protocols, effectiveCount]
  );

  return (
    <div
      className={styles.overlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.header}>
          <div className={styles.title}>
            <span>🗓️</span>
            <span>
              {isConvert
                ? t("CONVERT_TO_SERIES") || "הפוך לסדרה"
                : isSeries
                ? t("CONFIGURE_TREATMENT_PLAN") || "הגדר תוכנית טיפול"
                : t("CONFIGURE_PROTOCOLS") || "הגדר פרוטוקולים"}
            </span>
          </div>
          <button className={styles.closeBtn} onClick={onClose} type="button">
            ×
          </button>
        </div>

        <div className={styles.body}>
          {/* Convert mode — pick how many sessions the series should have. */}
          {isConvert && (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label className={seriesStyles.seriesLabel}>
                {t("NUMBER_OF_APPOINTMENTS") || "מספר מפגשים"}
              </label>
              <div className={seriesStyles.countButtons}>
                {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setConvertCount(num)}
                    className={`${seriesStyles.countButton} ${
                      convertCount === num ? seriesStyles.countButtonSelected : ""
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Template picker + series settings + plan details are for a multi-session
              plan only. A single appointment configures protocols alone. */}
          {isSeries && (
          <>
          {/* Choose from an existing plan template */}
          <div className={styles.templateRow}>
            <button
              type="button"
              className={styles.templateBtn}
              onClick={() => setShowTemplates(true)}
            >
              <span>📋</span>
              {t("SELECT_EXISTING_PROGRAM") || "בחר תוכנית קיימת"}
            </button>
          </div>

          {/* Section 1 — frequency + duration (read-only) */}
          <div className={seriesStyles.planCard}>
            <div className={seriesStyles.planCardHeader}>
              <span>
                {isSeries
                  ? t("SERIES_SETTINGS") || "הגדרות לסדרת המפגשים"
                  : t("APPOINTMENT_DURATION") || "משך הפגישה"}
              </span>
            </div>
            <div className={seriesStyles.seriesRow}>
              {isSeries && (
                <div className={seriesStyles.seriesField}>
                  <label className={seriesStyles.seriesLabel} htmlFor="tpm-sequence">
                    {t("APPOINTMENT_FREQUENCY")} *
                  </label>
                  <select
                    id="tpm-sequence"
                    className={seriesStyles.seriesSelect}
                    value={sequenceId || ""}
                    // Set by the patient's device protocol: the course already says how many
                    // sessions a week it runs, so this is shown to be read, not answered.
                    disabled={frequencyLocked}
                    onChange={(e) => setSequenceId(Number(e.target.value))}
                  >
                    <option value="">{t("SELECT_PLACEHOLDER")}</option>
                    {sequenceOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className={seriesStyles.seriesField}>
                <label className={seriesStyles.seriesLabel}>
                  {t("APPOINTMENT_DURATION")}
                </label>
                <div
                  style={{
                    padding: "9px 10px",
                    border: "1px solid #d9d9e3",
                    borderRadius: "8px",
                    backgroundColor: "#f5f5f5",
                    color: "#4b5563",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {calculatedDurationMinutes} {t("MINUTES_SUFFIX") || "דקות"}
                </div>
              </div>
            </div>
            <div className={styles.requiredHint}>
              {t("TREATMENT_PLAN_REQUIRED_HINT") ||
                "תדירות ומשך הם שדות חובה"}
            </div>
          </div>
          </>
          )}

          {/* Protocols — shown for both. For a series it's a collapsed accordion; for a
              single appointment it's the only content, shown directly. */}
          {isSeries ? (
            <div>
              <div
                className={seriesStyles.accordionHeader}
                onClick={() => setProtocolsOpen((o) => !o)}
              >
                <span>
                  {t("PROTOCOLS") || "פרוטוקולים"}
                  {" · "}
                  {t("OPTIONAL") || "אופציונלי"}
                  {draftProtocols.length > 0 ? ` (${draftProtocols.length})` : ""}
                </span>
                <span
                  className={`${seriesStyles.chevron} ${protocolsOpen ? seriesStyles.chevronOpen : ""}`}
                >
                  ▾
                </span>
              </div>
              {/* Kept mounted (display toggle) so protocol validation stays live. */}
              <div style={{ display: protocolsOpen ? "block" : "none", marginTop: 8 }}>
                <ProtocolsManager
                  repeatCount={effectiveCount}
                  protocols={protocols}
                  selectedProtocols={draftProtocols}
                  onChange={setDraftProtocols}
                  onValidationChange={setProtocolsValid}
                />
              </div>
            </div>
          ) : (
            <ProtocolsManager
              repeatCount={effectiveCount}
              protocols={protocols}
              selectedProtocols={draftProtocols}
              onChange={setDraftProtocols}
              onValidationChange={setProtocolsValid}
            />
          )}

          {isSeries && (
          <>
          {/* Plan details — optional, collapsed by default */}
          <div
            className={seriesStyles.accordionHeader}
            onClick={() => setDetailsOpen((o) => !o)}
          >
            <span>
              {t("PLAN_DETAILS") || "פרטי התוכנית"}
              {" · "}
              {t("OPTIONAL") || "אופציונלי"}
            </span>
            <span
              className={`${seriesStyles.chevron} ${detailsOpen ? seriesStyles.chevronOpen : ""}`}
            >
              ▾
            </span>
          </div>
          <div
            className={seriesStyles.planCard}
            style={{ display: detailsOpen ? "block" : "none", marginTop: 8 }}
          >
            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, color: "#7a7a8c", display: "block", marginBottom: "6px" }}>
                {t("NAME_1") || "שם התוכנית"}
              </label>
              <input
                type="text"
                placeholder={t("PLAN_NAME_PLACEHOLDER") || "השאר ריק לשם אוטומטי"}
                style={{
                  width: "100%",
                  padding: "9px 10px",
                  border: "1px solid #d9d9e3",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
                value={treatmentPlanName}
                onChange={(e) => setTreatmentPlanName(e.target.value)}
              />
            </div>
            <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "1fr 1fr" }}>
              {roleOptions.length > 0 && (
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#7a7a8c" }}>
                    {t("SUPPORT_ROLE") || "תפקיד"}
                  </label>
                  <select
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d9d9e3",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontFamily: "inherit",
                    }}
                    value={roleId || ""}
                    onChange={(e) => setRoleId(Number(e.target.value) || 0)}
                  >
                    <option value="">{t("SELECT_PLACEHOLDER")}</option>
                    {roleOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {genderOptions.length > 0 && (
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#7a7a8c" }}>
                    {t("GENDER") || "מין"}
                  </label>
                  <select
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d9d9e3",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontFamily: "inherit",
                    }}
                    value={genderId || ""}
                    onChange={(e) => setGenderId(Number(e.target.value) || 0)}
                  >
                    <option value="">{t("SELECT_PLACEHOLDER")}</option>
                    {genderOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {migzarOptions.length > 0 && (
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#7a7a8c" }}>
                    {t("SECTOR") || "סקטור"}
                  </label>
                  <select
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d9d9e3",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontFamily: "inherit",
                    }}
                    value={migzarId || ""}
                    onChange={(e) => setMigzarId(Number(e.target.value) || 0)}
                  >
                    <option value="">{t("SELECT_PLACEHOLDER")}</option>
                    {migzarOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {/* Meeting mode is taken from the appointment's media selection (read-only) */}
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#7a7a8c" }}>
                  {t("MEETING_MODE") || "אופן המפגש"}
                </label>
                <div
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d9d9e3",
                    borderRadius: "8px",
                    backgroundColor: "#f5f5f5",
                    color: "#4b5563",
                    fontSize: "13px",
                    boxSizing: "border-box",
                  }}
                >
                  {mediaLabel || "—"}
                </div>
              </div>
              {fundingSourcesOptions.length > 0 && (
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#7a7a8c" }}>
                    {t("FUNDING_SOURCE") || "מקור מימון"}
                  </label>
                  <select
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d9d9e3",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontFamily: "inherit",
                    }}
                    value={fundingSourcesId || ""}
                    onChange={(e) => setFundingSourcesId(Number(e.target.value) || 0)}
                  >
                    <option value="">{t("SELECT_PLACEHOLDER")}</option>
                    {fundingSourcesOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {treatmentTypesOptions.length > 0 && (
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#7a7a8c" }}>
                    {t("TREATMENT_TYPE") || "סוג הטיפול"}
                  </label>
                  <select
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d9d9e3",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontFamily: "inherit",
                    }}
                    value={treatmentTypesId || ""}
                    onChange={(e) => setTreatmentTypesId(Number(e.target.value) || 0)}
                  >
                    <option value="">{t("SELECT_PLACEHOLDER")}</option>
                    {treatmentTypesOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
          </>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} type="button">
            {t("APPOINTMENTS_CLOSE") || "ביטול"}
          </button>
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={!canSave}
            type="button"
          >
            {t("SAVE") || "שמור"}
          </button>
        </div>
      </div>

      {/* Existing-plan template picker — own overlay above this modal */}
      {showTemplates && (
        <div
          className={styles.templateOverlay}
          onClick={(e) => e.target === e.currentTarget && setShowTemplates(false)}
        >
          <div className={styles.templatePanel} dir={isRtl ? "rtl" : "ltr"}>
            <div className={styles.templatePanelHeader}>
              <span>{t("SELECT_EXISTING_PROGRAM") || "בחר תוכנית קיימת"}</span>
              <button
                className={styles.closeBtn}
                onClick={() => setShowTemplates(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <div className={styles.templatePanelBody}>
              <CompanyProgramsPopup
                companyId={companyId}
                onClick={handleTemplateSelect}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentPlanModal;
